import React, { useContext, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { keyBy, merge } from 'lodash';

import WDT_STUDENT_ERROR_QUERY from 'graph/reports/wdt-student-error-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import BarChart from 'components/@common/Charts/BarChart';
import ErrorTypePicker from '../../common/ErrorTypePicker';
import CharacterOrderAndFilter from '../../common/CharacterOrderAndFilter';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { CharacterErrorType, ModuleType } from '../../../../types';
import { getQueryVariables } from '../../../../utils/report-param-utils';
import { PrintViewContext } from '../../../../providers/PrintViewProvider';
import {
  WdtCharacterErrorData,
  WdtCharacterGroup,
  WdtCharacterOrder,
  WdtErrorData
} from 'types/schema-generated-types';
import {
  BAR_CHART_COMMON_OPTIONS,
  CHARACTER_ERROR_COLORS
} from '../../../../utils/report-wdt-utils';

interface ErrorAnalysisForCharactersChartViewProps {
  module: ModuleType;
}

const ErrorAnalysisForCharactersChartView: React.FC<ErrorAnalysisForCharactersChartViewProps> = ({
  module
}) => {
  const { fullChartWidth } = useContext(PrintViewContext);
  const [{ dateRange, entityType, entityId }] = useReportQueryParams();
  const [fetchReport, { data, loading }] = useLazyQuery(WDT_STUDENT_ERROR_QUERY);
  const { t } = useTranslation('Reports');
  const [characterOrder, setCharacterOrder] = useState<any>(WdtCharacterOrder.natural);
  const [characterGroup, setCharacterGroup] = useState<number>();
  const [errorTypeFilter, setErrorTypeFilter] = useState<any>();

  const reportData: WdtCharacterErrorData[] = data?.wdtStudentErrorsReport?.characterErrors ?? [];

  const characterGroups: WdtCharacterGroup[] = data?.wdtStudentErrorsReport?.characterGroups;

  const filterCharacterGroup = (item: WdtCharacterErrorData): boolean =>
    !characterGroup || item.character?.characterGroupIds?.includes(characterGroup);

  const filterErrorType = (error: WdtErrorData): boolean =>
    !errorTypeFilter || error.type === errorTypeFilter;

  const {
    characters,
    missedStartingPointSeries,
    startedWrongWaySeries,
    outOfBoundsSeries,
    liftedFingerSeries,
    reversedDirectionSeries
  } = reportData.filter(filterCharacterGroup).reduce(
    (acc, { character, errors }) => {
      if (!character) return acc;

      const errorsByType = keyBy(errors.filter(filterErrorType), 'type');

      acc.characters.push(character.character);
      acc.outOfBoundsSeries.push(errorsByType[CharacterErrorType.OUT_OF_BOUNDS]?.count ?? 0);
      acc.liftedFingerSeries.push(errorsByType[CharacterErrorType.LIFTED_FINGER]?.count ?? 0);
      acc.missedStartingPointSeries.push(
        errorsByType[CharacterErrorType.MISSED_STARTING_POINT]?.count ?? 0
      );
      acc.startedWrongWaySeries.push(
        errorsByType[CharacterErrorType.STARTED_WRONG_WAY]?.count ?? 0
      );
      acc.reversedDirectionSeries.push(
        errorsByType[CharacterErrorType.REVERSED_DIRECTION]?.count ?? 0
      );
      return acc;
    },
    {
      characters: [] as string[],
      missedStartingPointSeries: [] as number[],
      startedWrongWaySeries: [] as number[],
      outOfBoundsSeries: [] as number[],
      liftedFingerSeries: [] as number[],
      reversedDirectionSeries: [] as number[]
    }
  );

  const series = [
    {
      name: t(CharacterErrorType.MISSED_STARTING_POINT, { ns: 'CharacterErrorType' }),
      data: missedStartingPointSeries
    },
    {
      name: t(CharacterErrorType.STARTED_WRONG_WAY, { ns: 'CharacterErrorType' }),
      data: startedWrongWaySeries
    },
    {
      name: t(CharacterErrorType.OUT_OF_BOUNDS, { ns: 'CharacterErrorType' }),
      data: outOfBoundsSeries
    },
    {
      name: t(CharacterErrorType.LIFTED_FINGER, { ns: 'CharacterErrorType' }),
      data: liftedFingerSeries
    },
    {
      name: t(CharacterErrorType.REVERSED_DIRECTION, { ns: 'CharacterErrorType' }),
      data: reversedDirectionSeries
    }
  ];

  const options = merge({}, BAR_CHART_COMMON_OPTIONS, {
    colors: CHARACTER_ERROR_COLORS,
    yaxis: { title: { text: 'Error Count' } },
    xaxis: { categories: characters },
    plotOptions: { bar: { columnWidth: '100%' } },
    stroke: {
      show: true,
      colors: ['transparent'],
      width: 2
    }
  });

  useEffect(() => {
    fetchReport({
      variables: merge(getQueryVariables({ entityId, entityType, dateRange }), {
        where: {
          module,
          characterOrder
        }
      })
    });
  }, [fetchReport, entityId, entityType, dateRange, module, characterOrder]);

  const cardTitle = (
    <Box display="flex" justifyContent="flex-start" alignItems="center" mb={2}>
      <Typography variant="h4">{t(`Error for ${t(module, { ns: 'ModuleType' })}`)}</Typography>
      <Box
        flex={1}
        ml={1}
        display="grid"
        gridAutoFlow="column"
        justifyContent="flex-start"
        alignItems="center"
        gridGap="1rem"
      >
        {module !== ModuleType.NUMBERS && (
          <CharacterOrderAndFilter
            characterGroups={characterGroups}
            order={characterOrder}
            groupId={characterGroup}
            setOrder={setCharacterOrder}
            setGroupId={setCharacterGroup}
          />
        )}
      </Box>
      <Typography component="label">{t(`Error Type`)}</Typography>
      <Box ml={1} width="200px">
        <ErrorTypePicker
          value={errorTypeFilter}
          onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
            setErrorTypeFilter(event.target.value)
          }
        />
      </Box>
    </Box>
  );

  return (
    <ReportCard title={cardTitle} loading={loading} reportData={reportData}>
      <BarChart height={250} width={fullChartWidth} series={series} options={options} />
    </ReportCard>
  );
};

export default ErrorAnalysisForCharactersChartView;
